import * as Sentry from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import { AGGREGATE_NAMES, LOAN_REQUEST_COMMANDS } from 'cpool-constants';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { verifyCensorCoApplicantStatus } from '../utils/verifyCensorCoApplicantStatus';
import { APICommunicator } from '~/api/APICommunicator';
import { useChainAwareInvalidation } from '~/hooks/useChainAwareInvalidation';
import useUserInfo from '~/hooks/useUserInfo';
import { queryKeys } from '~/utils/queryKeys';

export const useCensorCoApplicantMutation = (loanRequestId: string) => {
    const user = useUserInfo();
    const { t } = useTranslation();
    const invalidateOnChainComplete = useChainAwareInvalidation();
    return useMutation({
        mutationFn: async ({ applicantId }: { applicantId: string }) => {
            return APICommunicator.sendCommand({
                aggregateId: loanRequestId,
                aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
                commandName: LOAN_REQUEST_COMMANDS.censorCoApplicant,
                payload: {
                    applicantId,
                    issuer: user?.id,
                },
            });
        },
        onError: (error) => {
            Sentry.captureException(error);
            toast.error(t('STATUS.CENSOR_CO_APPLICANT_TOASTS.REQUEST_ERROR'));
        },
        onSuccess: ({ id }) => {
            toast.info(t('STATUS.CENSOR_CO_APPLICANT_TOASTS.REQUEST_SUCCESS'));
            // wait for the censor chain to finish, then refetch and report the final outcome once
            invalidateOnChainComplete({
                correlationId: id,
                onComplete: () => {
                    verifyCensorCoApplicantStatus({ loanRequestId, t });
                },
                queryKeys: [
                    queryKeys.get(loanRequestId),
                    queryKeys.getAclPermissions({ loanRequestId }),
                    queryKeys.getSchufa(loanRequestId),
                    queryKeys.getApplicantNotesByLeadId(loanRequestId),
                    queryKeys.getConsorsNotesByLeadId(loanRequestId),
                    queryKeys.getLoanRequestNotes(loanRequestId),
                    queryKeys.getSystemNotes(loanRequestId),
                ],
            });
        },
    });
};
